import { Image, Text } from "@sitecore-content-sdk/nextjs";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";
import {
  BrandPillarProps,
  PillarElementResult,
} from "components/brand-pillar/brand-pillar.props";
import styles from "./BrandPillar.module.scss";
import Button from "src/atoms/Button";

export const Default = (props: BrandPillarProps) => {
  console.log("---->[BrandPillar] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields?.data?.datasource) {
    return <NoDataFallback componentName="Brand Pillar" />;
  }

  const { Title, Description, HoverImage, CTA, pillarElement } =
    fields.data.datasource;

  const titleParam = Title?.jsonValue;
  const descriptionParam = Description?.jsonValue;
  const hoverImageParam = HoverImage?.jsonValue;
  const ctaParam = CTA?.jsonValue;
  const pillarItems = pillarElement?.pillarElementResult ?? [];

  const pillarBarStyle = isPageEditing
    ? ""
    : "lg:min-h-[398px] mt-[var(--spacing-thirtytwo)] mb-[var(--global-component-spacing)]";
  const highlightBoxStyle = isPageEditing
    ? ""
    : `${styles.highlightBox} lg:mb-[0px] lg:w-[50%] lg:absolute lg:last:right-0 min-h-[1px] lg:max-h-full`;
  const hoverImageStyle = isPageEditing ? "" : "lg:block";
  const imageWrapperStyle = isPageEditing
    ? styles.imageWrapper
    : `${styles.imageWrapper} lg:max-h-[398px]`;
  const highlightHeaderStyle = isPageEditing
    ? ""
    : "lg:top-[0px] lg:absolute lg:inset-0 lg:flex lg:items-center";
  const headerTitleStyle = isPageEditing ? "" : styles.headerCont;
  const captionStyle = isPageEditing
    ? ""
    : `${styles.caption} lg:mb-0 lg:absolute lg:inset-0 lg:flex lg:items-center lg:p-1`;
  const highlightBodyStyle = isPageEditing ? "" : "lg:text-white lg:text-xl";
  const pillarContainerStyle = isPageEditing
    ? styles.pageEditingView
    : styles.FeatureContainer;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div
      className={`brand-pillar-component ${pillarContainerStyle} mx-auto pt-5 lg:w-352 lg:max-w-full ${hiddenBlockStyles}`}
      data-testid="brand-pillar"
      data-role="brand-pillar-component"
    >
      {hoverImageParam?.value?.src && !isPageEditing && (
        <link rel="preload" href={hoverImageParam.value.src} as="image" />
      )}

      <div className="componentContent px-4 m-auto w-full">
        <h2 className="headingContent text-center mt-0 text-ecolab-blue">
          <Text
            tag="span"
            className="font-bold block"
            field={titleParam}
            data-testid="header-title"
          />
          <Text
            tag="span"
            className="block"
            field={descriptionParam}
            data-testid="header-description"
          />
        </h2>
        <div
          className={`pillarBar w-full relative overflow-hidden ${pillarBarStyle}`}
        >
          {pillarItems.map((item: PillarElementResult, index: number) => {
            const backgroundSrc = item?.BackgroundImage?.jsonValue?.value?.src;
            return (
              <div
                className={`mb-6 overflow-hidden ${highlightBoxStyle} ${
                  styles[`pillar${index}`]
                }`}
                key={`${item?.Title?.jsonValue?.value}-${index}`}
                data-testid="pillar-images"
                tabIndex={0}
              >
                <div className={`${imageWrapperStyle} relative`}>
                  {backgroundSrc && !isPageEditing && (
                    <link rel="preload" href={backgroundSrc} as="image" />
                  )}
                  <Image
                    field={item?.BackgroundImage?.jsonValue}
                    className="w-full lg:w-auto min-h-57.5 lg:h-99.5 object-cover"
                    data-testid="slide-image"
                    loading="eager"
                    imageParams={{ mw: 688 }}
                  />
                  <Image
                    field={hoverImageParam}
                    className={`${styles.hoverImage} min-h-57.5 lg:h-99.5 object-cover absolute inset-0 w-full lg:w-auto hidden ${hoverImageStyle}`}
                    data-testid="slide-image"
                    loading="eager"
                    imageParams={{ mw: 688 }}
                  />
                </div>
                <div
                  className={`${styles.highlightHeader} flex relative -top-11.5 ${
                    !item?.Title?.jsonValue?.value ? "mb-6 lg:mb-0" : ""
                  } ${highlightHeaderStyle}`}
                >
                  <Text
                    tag="div"
                    className={`bg-text-gray-opacity text-white px-1.5 text-3xl/[inherit] font-medium relative leading-[1.2] lg:leading-[1.4] lg:max-w-70 line-clamp-1 ${headerTitleStyle}`}
                    field={item?.Title?.jsonValue}
                    tabIndex={0}
                    data-testid="slide-title"
                  />
                </div>
                <div
                  className={`mb-0 ${captionStyle} ${styles.captionBoxStyle}`}
                >
                  <div
                    className={`${styles.highlightBody} text-text-gray text-xl font-medium  ${
                      isPageEditing ? "[&_span]:z-2 text-white" : ""
                    }
                   ${highlightBodyStyle}`}
                  >
                    {isPageEditing && (
                      <Image
                        field={hoverImageParam}
                        className={`${styles.hoverImage} object-cover absolute inset-0 w-full lg:w-auto`}
                        imageParams={{ mw: 1200 }}
                      />
                    )}
                    <Text
                      tag="p"
                      className={`text-center text-base font-normal lg:text-left lg:text-xl lg:font-medium leading-[1.4] line-clamp-5 ${styles.highlightBodyContent}`}
                      field={item?.HoverText?.jsonValue}
                      data-testid="slide-desc"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-center text-center mt-0 mb-(--global-component-spacing) justify-self-center">
          <Button
            buttonType={
              ctaParam?.value?.class ? ctaParam.value.class : "primary"
            }
            className=""
            field={ctaParam}
            iconName="CHEVRON_BLUE_RIGHT"
            tabIndex={0}
            aria-label={ctaParam?.value?.text}
            data-role="link"
          />
        </div>
      </div>
    </div>
  );
};
